import {
  Button,
  Form,
  FormContent,
  Input,
  InputNumber,
  message,
  Select,
  SubContent,
  Table,
  Wrapper,
  useBreadcrumbRoutes,
} from '@maxtropy/components';
import {
  apiV2AirCompressorOverviewGetAirCompressorListPost,
  apiV2AirCompressorOverviewGetAirCompressorStationListPost,
  apiV2AirCompressorOverviewGetCircuitListPost,
  apiV2AirCompressorOverviewGetConfigPost,
  apiV2AirCompressorOverviewGetFlowMeterPost,
  apiV2AirCompressorOverviewSaveConfigPost,
  apiV2AirStationConfigScadaListPost,
} from '@maxtropy/device-customer-apis-v2';
import { Col, Row, Space, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EditDeviceModal from './EditDeviceModal';
import styles from './index.module.scss';
import { CacheData, DeviceRow, OptionItem, OverviewFormValues } from './types';
import {
  branchPipeKeyMap,
  DEFAULT_OVERVIEW_TITLE,
  deviceTypeLabelMap,
  deviceTypeOptions,
  mainConfigList,
  mainDeviceKeyMap,
  mainThresholdKeyMap,
  MAX_OVERVIEW_TITLE_LENGTH,
  specOptions,
  stationConfigList,
  stationKeyMap,
  stationStatusKeyMap,
} from './utils';

const routes = [{ name: '空压站总览配置' }];

const getInitialFormValues = (): OverviewFormValues => ({
  title: DEFAULT_OVERVIEW_TITLE,
  mainDevice1: undefined,
  mainThreshold1: undefined,
  mainDevice2: undefined,
  mainThreshold2: undefined,
  mainDevice3: undefined,
  mainThreshold3: undefined,
  station1: undefined,
  station2: undefined,
  station3: undefined,
  station4: undefined,
  branchPipe1: [],
  branchPipe2: [],
  branchPipe3: [],
  branchPipe4: [],
  stationStatus1: undefined,
  stationStatus2: undefined,
  stationStatus3: undefined,
  stationStatus4: undefined,
});

const formatStationName = (name?: string) => {
  if (!name) return '--';
  if (name.includes('#空压站')) return name;

  const matched = name.match(/(\d+)/);
  if (matched?.[1]) {
    return `${matched[1]}#空压站`;
  }

  return name;
};

const buildStationSequenceMap = (values: OverviewFormValues) => {
  const stationSequenceMap = new Map<number, number>();
  stationConfigList.forEach(sequence => {
    const stationId = values[stationKeyMap[sequence]];
    if (stationId !== undefined && stationId !== null) {
      stationSequenceMap.set(stationId, sequence);
    }
  });
  return stationSequenceMap;
};

const OverviewConfig: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [pageLoading, setPageLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deviceRows, setDeviceRows] = useState<DeviceRow[]>([]);
  const [editingDevice, setEditingDevice] = useState<DeviceRow>();
  const [mainDeviceOptions, setMainDeviceOptions] = useState<OptionItem[]>([]);
  const [stationOptions, setStationOptions] = useState<OptionItem[]>([]);
  const [branchPipeOptions, setBranchPipeOptions] = useState<OptionItem[]>([]);
  const [stationStatusOptions, setStationStatusOptions] = useState<OptionItem[]>([]);

  const cacheRef = useRef<CacheData>({
    formValues: getInitialFormValues(),
    deviceRows: [],
  });

  const commonSelectProps = {
    placeholder: '请选择',
    showSearch: true,
    optionFilterProp: 'label' as const,
    filterOption: (input: string, option?: { label?: string | number }) =>
      String(option?.label ?? '')
        .toLowerCase()
        .includes(input.toLowerCase()),
    disabled: !isEdit,
    allowClear: true,
  };

  const getExclusiveOptions = (
    options: OptionItem[],
    selectedValues: Array<number | undefined>,
    currentValue?: number
  ) => {
    const selectedSet = new Set(
      selectedValues.filter((value): value is number => value !== undefined && value !== null)
    );
    return options.map(option => ({
      ...option,
      disabled: option.value !== currentValue && selectedSet.has(option.value),
    }));
  };

  const mapConfigToDeviceRows = useCallback(
    (
      stationList:
        | Exclude<Awaited<ReturnType<typeof apiV2AirCompressorOverviewGetAirCompressorListPost>>['list'], undefined>
        | undefined,
      configList:
        | Exclude<
            Awaited<ReturnType<typeof apiV2AirCompressorOverviewGetConfigPost>>['airCompressorConfigs'],
            undefined
          >
        | undefined,
      stationSequenceMap?: Map<number, number>
    ): DeviceRow[] => {
      const flattenedRows = (stationList ?? []).flatMap((stationItem, stationIndex) =>
        (stationItem.airCompressors ?? []).map((airCompressor, airIndex) => {
          const stationId = stationItem.station?.id;
          const stationSequence = stationId !== undefined ? stationSequenceMap?.get(stationId) : undefined;
          return {
            id: airCompressor.id ?? Number(`${stationIndex + 1}${airIndex + 1}`),
            airCompressorId: airCompressor.id,
            stationId,
            machineName: airCompressor.name ?? '--',
            stationName: stationSequence ? `${stationSequence}#空压站` : formatStationName(stationItem.station?.name),
          };
        })
      );

      return flattenedRows.map(item => {
        const configItem = configList?.find(
          config =>
            config.airCompressor?.id === item.airCompressorId && config.airCompressorStation?.id === item.stationId
        );
        return {
          ...item,
          mainTransformerBelong: configItem?.mainTransformerBelong,
          mainTransformer: configItem?.mainTransformerBelong ? `${configItem.mainTransformerBelong}#主变` : undefined,
          typeValue: configItem?.type,
          type: configItem?.type ? deviceTypeLabelMap[configItem.type] : undefined,
          specValue: configItem?.spec,
          specification: configItem?.spec,
          runDurationWeight: configItem?.runtimeWeight,
          isMaintenance: configItem?.isMaintenance ?? false,
        };
      });
    },
    []
  );

  const loadPageData = useCallback(async () => {
    setPageLoading(true);
    try {
      const [circuitRes, stationRes, flowMeterRes, scadaRes, configRes] = await Promise.all([
        apiV2AirCompressorOverviewGetCircuitListPost({}),
        apiV2AirCompressorOverviewGetAirCompressorStationListPost({}),
        apiV2AirCompressorOverviewGetFlowMeterPost({}),
        apiV2AirStationConfigScadaListPost({}),
        apiV2AirCompressorOverviewGetConfigPost({}),
      ]);

      setMainDeviceOptions(
        (circuitRes.list ?? []).map(item => ({
          label: item.name!,
          value: item.id!,
        }))
      );
      setStationOptions(
        (stationRes.list ?? []).map(item => ({
          label: item.name!,
          value: item.id!,
        }))
      );
      setBranchPipeOptions(
        (flowMeterRes.list ?? []).map(item => ({
          label: item.name!,
          value: item.id!,
        }))
      );
      setStationStatusOptions(
        (scadaRes.list ?? []).map(item => ({
          label: item.airStationName!,
          value: item.id!,
        }))
      );

      const nextFormValues = getInitialFormValues();
      nextFormValues.title = configRes.title || DEFAULT_OVERVIEW_TITLE;

      (configRes.mainTransformerConfigs ?? []).forEach(item => {
        const sequence = item.sequence;
        if (!sequence || !(sequence in mainDeviceKeyMap)) return;
        nextFormValues[mainDeviceKeyMap[sequence as keyof typeof mainDeviceKeyMap]] = item.gridBillingCircuit?.id;
        nextFormValues[mainThresholdKeyMap[sequence as keyof typeof mainThresholdKeyMap]] = item.demandThreshold;
      });

      (configRes.airCompressorStationConfigs ?? []).forEach(item => {
        const sequence = item.sequence;
        if (!sequence || !(sequence in stationKeyMap)) return;
        nextFormValues[stationKeyMap[sequence as keyof typeof stationKeyMap]] = item.airCompressorStation?.id;
        nextFormValues[branchPipeKeyMap[sequence as keyof typeof branchPipeKeyMap]] = (item.branchPipes ?? [])
          .filter(pipe => pipe.id)
          .map(pipe => pipe.id!);
        nextFormValues[stationStatusKeyMap[sequence as keyof typeof stationStatusKeyMap]] = item.station?.id;
      });

      form.setFieldsValue(nextFormValues);

      const stationIds = stationConfigList
        .map(sequence => nextFormValues[stationKeyMap[sequence]])
        .filter((value): value is number => value !== undefined && value !== null);

      let nextDeviceRows: DeviceRow[] = [];
      if (stationIds.length) {
        const stationSequenceMap = buildStationSequenceMap(nextFormValues);
        const airCompressorRes = await apiV2AirCompressorOverviewGetAirCompressorListPost({ list: stationIds });
        nextDeviceRows = mapConfigToDeviceRows(
          airCompressorRes.list,
          configRes.airCompressorConfigs,
          stationSequenceMap
        );
      }

      setDeviceRows(nextDeviceRows);
      cacheRef.current = {
        formValues: nextFormValues,
        deviceRows: nextDeviceRows,
      };
    } finally {
      setPageLoading(false);
    }
  }, [form, mapConfigToDeviceRows]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const refreshAirCompressorRows = useCallback(
    async (allValues: OverviewFormValues, keepConfigsFromPrev = true) => {
      const stationIds = stationConfigList
        .map(sequence => allValues[stationKeyMap[sequence]])
        .filter((value): value is number => value !== undefined && value !== null);

      if (!stationIds.length) {
        setDeviceRows([]);
        return;
      }

      const res = await apiV2AirCompressorOverviewGetAirCompressorListPost({ list: stationIds });
      const stationSequenceMap = buildStationSequenceMap(allValues);
      const baseRows = mapConfigToDeviceRows(res.list, [], stationSequenceMap);

      if (!keepConfigsFromPrev) {
        setDeviceRows(baseRows);
        return;
      }

      setDeviceRows(prev =>
        baseRows.map((item, index) => {
          const prevItem = prev.find(row => row.airCompressorId === item.airCompressorId) ?? prev[index];
          return prevItem
            ? {
                ...item,
                mainTransformerBelong: prevItem.mainTransformerBelong,
                mainTransformer: prevItem.mainTransformer,
                typeValue: prevItem.typeValue,
                type: prevItem.type,
                specValue: prevItem.specValue,
                specification: prevItem.specification,
                runDurationWeight: prevItem.runDurationWeight,
                isMaintenance: prevItem.isMaintenance,
              }
            : item;
        })
      );
    },
    [mapConfigToDeviceRows]
  );

  const handleSave = async () => {
    const values = (await form.validateFields()) as OverviewFormValues;
    setSaveLoading(true);
    try {
      await apiV2AirCompressorOverviewSaveConfigPost({
        title: values.title,
        mainTransformerConfigs: mainConfigList.map(sequence => ({
          sequence,
          gridBillingCircuitId: values[mainDeviceKeyMap[sequence]],
          demandThreshold: values[mainThresholdKeyMap[sequence]],
        })),
        airCompressorStationConfigs: stationConfigList.map(sequence => ({
          sequence,
          airCompressorStationId: values[stationKeyMap[sequence]],
          branchPipeIds: values[branchPipeKeyMap[sequence]],
          stationId: values[stationStatusKeyMap[sequence]],
        })),
        airCompressorConfigs: deviceRows.map(item => ({
          airCompressorId: item.airCompressorId,
          airCompressorStationId: item.stationId,
          mainTransformerBelong: item.mainTransformerBelong,
          type: item.typeValue,
          spec: item.specValue,
          runtimeWeight: item.runDurationWeight,
          isMaintenance: item.isMaintenance ?? false,
        })),
      });
      await loadPageData();
      setIsEdit(false);
      message.success('保存成功');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      return;
    }
    form.setFieldsValue(cacheRef.current.formValues);
    setDeviceRows(cacheRef.current.deviceRows);
    setIsEdit(false);
  };

  const handleDeviceSave = async (values: Partial<DeviceRow>) => {
    setDeviceRows(prev =>
      prev.map(item =>
        item.id === editingDevice?.id
          ? {
              ...item,
              ...values,
              mainTransformer: values.mainTransformerBelong
                ? `${values.mainTransformerBelong}#主变`
                : item.mainTransformer,
              typeValue: values.typeValue ?? item.typeValue,
              type: values.typeValue ? deviceTypeLabelMap[values.typeValue] : item.type,
              specValue: values.specValue ?? item.specValue,
              specification: values.specValue ?? item.specification,
              isMaintenance: values.isMaintenance ?? item.isMaintenance ?? false,
            }
          : item
      )
    );
    setEditingDevice(undefined);
    message.success('设备配置已更新');
  };

  const columns = [
    {
      title: '空压机',
      dataIndex: 'machineName',
      width: 180,
    },
    {
      title: '空压站',
      dataIndex: 'stationName',
      width: 160,
    },
    {
      title: '主变归属',
      dataIndex: 'mainTransformer',
      width: 160,
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 160,
    },
    {
      title: '规格',
      dataIndex: 'specification',
      width: 140,
    },
    {
      title: '是否维护',
      dataIndex: 'isMaintenance',
      width: 100,
      render: (_: unknown, record: DeviceRow) => (record.isMaintenance ? '是' : '否'),
    },
    {
      title: '运行时长权重',
      dataIndex: 'runDurationWeight',
      width: 180,
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 100,
      render: (_: unknown, record: DeviceRow) => (
        <Button type="link" disabled={!isEdit} onClick={() => setEditingDevice(record)}>
          编辑
        </Button>
      ),
    },
  ];

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]} className={styles.wrapper}>
      <Spin spinning={pageLoading || saveLoading}>
        <Form
          form={form}
          initialValues={getInitialFormValues()}
          onValuesChange={(_, allValues) => {
            const changedFieldNames = Object.keys(_);
            if (changedFieldNames.some(fieldName => stationConfigList.some(index => fieldName === `station${index}`))) {
              void refreshAirCompressorRows(allValues as OverviewFormValues);
            }
          }}
        >
          <FormContent narrow title="空压站总览配置">
            <SubContent title="总览组态配置">
              <Row gutter={48}>
                <Col span={12}>
                  <Form.Item
                    label="总览组态标题"
                    name="title"
                    rules={[
                      { required: true, whitespace: true, message: '请输入总览组态标题' },
                      { max: MAX_OVERVIEW_TITLE_LENGTH, message: `最多输入${MAX_OVERVIEW_TITLE_LENGTH}个字符` },
                    ]}
                  >
                    <Input disabled={!isEdit} maxLength={MAX_OVERVIEW_TITLE_LENGTH} placeholder="请输入" showCount />
                  </Form.Item>
                </Col>
              </Row>
            </SubContent>

            <SubContent title="主要参数配置">
              {mainConfigList.map(index => (
                <Row gutter={48} key={index}>
                  <Col span={12}>
                    <Form.Item
                      label={`主变${index}`}
                      required
                      dependencies={mainConfigList.map(item => `mainDevice${item}`)}
                    >
                      {({ getFieldValue }) => {
                        const selectedValues = mainConfigList.map(
                          item => getFieldValue(`mainDevice${item}`) as number | undefined
                        );
                        const currentValue = getFieldValue(`mainDevice${index}`) as number | undefined;

                        return (
                          <Form.Item
                            noStyle
                            name={`mainDevice${index}`}
                            rules={[{ required: true, message: `请选择主变${index}` }]}
                          >
                            <Select
                              options={getExclusiveOptions(mainDeviceOptions, selectedValues, currentValue)}
                              placeholder="请选择"
                              showSearch
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                String(option?.label ?? '')
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                              disabled={!isEdit}
                              allowClear
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={`主变需量阈值${index}`}
                      name={`mainThreshold${index}`}
                      rules={[{ required: true, message: '请输入主变需量阈值' }]}
                    >
                      <InputNumber
                        disabled={!isEdit}
                        min={0}
                        max={99999999.99}
                        precision={2}
                        style={{ width: '100%' }}
                        addonAfter="kW"
                        placeholder="请输入"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </SubContent>

            <SubContent title="空压站配置">
              <Row gutter={[48, 0]}>
                {stationConfigList.map(index => (
                  <Col span={12} key={index}>
                    <Form.Item
                      label={`${index}#空压站`}
                      required
                      dependencies={stationConfigList.map(item => `station${item}`)}
                    >
                      {({ getFieldValue }) => {
                        const selectedValues = stationConfigList.map(
                          item => getFieldValue(`station${item}`) as number | undefined
                        );
                        const currentValue = getFieldValue(`station${index}`) as number | undefined;

                        return (
                          <Form.Item
                            noStyle
                            name={`station${index}`}
                            rules={[{ required: true, message: `请选择${index}#空压站` }]}
                          >
                            <Select
                              options={getExclusiveOptions(stationOptions, selectedValues, currentValue)}
                              {...commonSelectProps}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item
                      label={`${index}#空压站下分管`}
                      name={`branchPipe${index}`}
                      rules={[{ required: true, message: `请选择${index}#空压站下分管` }]}
                    >
                      <Select mode="multiple" options={branchPipeOptions} {...commonSelectProps} />
                    </Form.Item>
                    <Form.Item
                      label={`${index}#空压站组态`}
                      name={`stationStatus${index}`}
                      rules={[{ required: true, message: `请选择${index}#空压站组态` }]}
                    >
                      <Select options={stationStatusOptions} {...commonSelectProps} />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </SubContent>

            <SubContent title="空压机配置">
              <Table rowKey="id" columns={columns} dataSource={deviceRows} pagination={false} />
            </SubContent>
          </FormContent>

          <Space className="sticky-footer" size={8}>
            {isEdit ? (
              <>
                <Button type="primary" loading={saveLoading} onClick={handleSave}>
                  保存
                </Button>
                <Button onClick={handleCancel}>取消</Button>
              </>
            ) : (
              <>
                <Button type="primary" onClick={() => setIsEdit(true)}>
                  编辑
                </Button>
                <Button onClick={() => navigate('/power/monitoring/compressedAirOverview')}>返回</Button>
              </>
            )}
          </Space>
        </Form>
      </Spin>

      <EditDeviceModal
        open={!!editingDevice}
        record={editingDevice}
        mainTransformerOptions={mainConfigList.map(item => ({
          label: `${item}#主变`,
          value: item,
        }))}
        deviceTypeOptions={deviceTypeOptions}
        specOptions={specOptions}
        onCancel={() => setEditingDevice(undefined)}
        onSave={handleDeviceSave}
      />
    </Wrapper>
  );
};

export default OverviewConfig;
